import { Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, TextField } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import { Card } from "../../components/general/card";
import { useNavigate } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import { NAVIGATION_PATH } from "../../enums/navigation_enum";
import { useQueryClient } from "@tanstack/react-query";
import ElionLogo from "../../images/Elion_Logo_WordmarkBaseline_RGB_Red.svg";
import { useAmplitudeTrack } from "../../scripts/amplitude_utils";
import TermsAndConditionsDialog from "../../components/dialogs/terms_and_conditions_dialog";

export default function LoginPage() {
  const backend = useBackend();

  const username = React.useRef();
  const password = React.useRef();
  const keepLoggedIn = React.useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsData, setTermsData] = useState(null);

  const navigate = useNavigate();
  const { trackButtonClicked } = useAmplitudeTrack();
  const queryClient = useQueryClient();

  // Check if user is already logged in but needs to accept T&C
  React.useEffect(() => {
    if (backend.termsRequired) {
      setTermsData(backend.termsRequired);
      setShowTermsDialog(true);
    }
  }, [backend.termsRequired]);

  const onLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    queryClient.clear();
    const result = await backend.tryLogin(username.current.value, password.current.value, keepLoggedIn.current.checked);

    if (result.success) {
      if (result.requiresTerms) {
        // Show T&C dialog
        setTermsData(result.termsData);
        setShowTermsDialog(true);
        setIsLoading(false);
      } else {
        // Navigate to home
        navigate(NAVIGATION_PATH.HOME);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleTermsAccept = () => {
    // After accepting terms, clear the terms requirement and navigate to home
    setShowTermsDialog(false);
    backend.clearTermsRequired();
    navigate(NAVIGATION_PATH.HOME);
  };

  const handleTermsDecline = () => {
    // User declined terms, logout and stay on login page
    setShowTermsDialog(false);
    backend.tryLogout();
    setIsLoading(false);
  };

  return (
    <Container
      maxWidth={"xs"}
      sx={{ height: "100vh", display: "flex", justifyContent: "center", flexDirection: "column" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
        <img src={ElionLogo} alt="Elion Logo" style={{ height: 150 }} />
      </Box>
      <Card
        title="Login"
        sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
        component="form"
        onSubmit={onLogin}
      >
        <TextField
          inputRef={username}
          variant="outlined"
          placeholder="Username"
          name="username"
          autoComplete="username"
          sx={{ width: "100%", my: 1 }}
          required={true}
        />
        <TextField
          inputRef={password}
          variant="outlined"
          placeholder="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          sx={{ width: "100%", my: 1 }}
          required={true}
        />
        <Box
          component="span"
          onClick={() => {
            trackButtonClicked("forgot_password", "login_page");
            navigate(NAVIGATION_PATH.REQUEST_PASSWORD_RESET);
          }}
          sx={{
            alignSelf: "flex-end",
            fontSize: "11px",
            cursor: "pointer",
            textDecoration: "underline",
            color: "primary.main",
          }}
        >
          Forgot password?
        </Box>

        <FormControlLabel
          control={<Checkbox defaultChecked inputRef={keepLoggedIn} />}
          label="Keep me logged in"
          sx={{ mt: 1 }}
        />

        {!isLoading ? (
          <Button sx={{ mt: 2, width: "100%" }} variant="contained" type="submit" disabled={isLoading}>
            Login
          </Button>
        ) : (
          <CircularProgress size={24} />
        )}
      </Card>

      {/* Terms and Conditions Dialog */}
      <TermsAndConditionsDialog
        open={showTermsDialog}
        termsData={termsData}
        backend={backend}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
      />
    </Container>
  );
}
